/**
 * ============================================================================
 * USER ACTIVITY COMPONENT - 100% Ready
 * ============================================================================
 * 
 * User activity timeline component
 */

import React, { useEffect, useState } from 'react';
import { useUser } from '../../hooks/useUser';
import { useI18n } from '../../providers';
import Spinner from '../common/Spinner';
import EmptyState from '../common/EmptyState';
import Avatar from '../common/Avatar';

/**
 * User Activity Component
 * @param {Object} props
 * @param {string} props.period - Activity period (week, month, year, all)
 * @returns {JSX.Element}
 */
const UserActivity = ({ period = 'month' }) => {
  const { loadActivity, activity, loading } = useUser();
  const { formatDate, formatTime, language } = useI18n();

  useEffect(() => {
    loadActivity({ period });
  }, [loadActivity, period]);

  const getActivityIcon = (type) => {
    const icons = {
      transaction_created: '📦',
      transaction_completed: '✅',
      payment_received: '💰',
      dispute_raised: '⚠️',
      rating_given: '⭐',
      profile_updated: '👤',
    };
    return icons[type] || '📊';
  };

  const getActivityColor = (type) => {
    const colors = {
      transaction_created: 'from-blue-500 to-cyan-500',
      transaction_completed: 'from-green-500 to-emerald-500',
      payment_received: 'from-yellow-500 to-orange-500',
      dispute_raised: 'from-red-500 to-pink-500',
      rating_given: 'from-purple-500 to-pink-500',
      profile_updated: 'from-gray-500 to-slate-500',
    };
    return colors[type] || 'from-gray-500 to-slate-500';
  };

  const cardClass = 'rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#111] p-4';

  if (loading && activity.length === 0) {
    return (
      <div className={`${cardClass} p-12`}>
        <div className="flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (activity.length === 0) {
    return (
      <div className={`${cardClass} p-12`}>
        <EmptyState
          icon="📊"
          title={language === 'fr' ? 'Aucune activité' : 'No activity'}
          description={
            language === 'fr'
              ? 'Vous n\'avez pas encore d\'activité récente'
              : "You don't have any recent activity yet"
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fadeInUp">
      {activity.map((item, index) => (
        <div
          key={item.id || index}
          className="flex items-start gap-4 p-4 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#111] hover:bg-[#1a1a1a] transition-colors animate-fadeInUp"
          style={{ animationDelay: `${index * 0.05}s` }}
        >
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getActivityColor(item.type)} flex items-center justify-center text-xl flex-shrink-0 shadow-lg`}>
            {getActivityIcon(item.type)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-[#fafafa] mb-1">
              {item.description || item.title || item.type}
            </p>
            <div className="flex items-center gap-2 text-xs text-[#a3a3a3]">
              {item.createdAt && (
                <>
                  <span>{formatDate(item.createdAt)}</span>
                  <span>•</span>
                  <span>{formatTime(item.createdAt)}</span>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default UserActivity;
